/**
 * When a failed query is worth asking again.
 *
 * The backend spins down when idle and a cold start takes 30-60s. The old
 * `retry: 1` put both attempts inside a second, so a WAKING backend looked
 * exactly like a dead one and every panel on the page showed its
 * permanent-failure state at once. That is what "Failed to load recordings"
 * was — the endpoint was healthy the whole time, and it cost a real debugging
 * detour before anyone measured it.
 *
 * Retry only what waking actually looks like. A 4xx is an answer; repeating
 * the question does not change it, and retrying a 401 five times just delays
 * the sign-in prompt.
 */

export const MAX_QUERY_RETRIES = 5;

/** Statuses that mean "the server isn't ready", not "the request was wrong". */
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

export function shouldRetryQuery(failureCount, error) {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  const status = error?.status;
  // No status at all = network error, CORS failure, or an aborted fetch —
  // which is what a spun-down host looks like from the browser.
  if (status === undefined || status === null) return true;
  return RETRYABLE_STATUSES.has(status);
}

/**
 * 1s, 2s, 4s, 8s, 8s — roughly 23s of patience. Long enough for a warm-ish
 * start, short enough that a genuinely dead backend doesn't feel hung.
 */
export function queryRetryDelay(attemptIndex) {
  return Math.min(1000 * 2 ** attemptIndex, 8000);
}
